import axios from 'axios'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { Alert } from '../../components/Alert'
import { AuthLayout } from '../../components/AuthLayout'
import { Button } from '../../components/Button'
import { Input } from '../../components/FormElements/Input'
import { api } from '../../services/api'
import { getErrorMessage } from '../../utils/getErrorMessage'
import { createForgotPasswordSchema } from '../../validators/forgotPasswordSchema'

type FormValues = {
  email: string
}

const schema = createForgotPasswordSchema({
  email: {
    email: 'E-mail inválido',
    required: 'Campo obrigatório',
  },
})

export const ForgotPassword = () => {
  const navigate = useNavigate()

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const { control, formState, handleSubmit } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const ondSendRecoverPasswordEmail = async (data: FormValues) => {
    try {
      await api.post('/auth/forgot-password', data)
      setSuccess('E-mail enviado com sucesso!')
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 404) {
          setError('E-mail não encontrado')
          return
        }
      }

      setError(getErrorMessage(error))
    }
  }

  useEffect(() => {
    if (formState.isSubmitting) {
      setError(null)
      setSuccess(null)
    }
  }, [formState.isSubmitting])

  return (
    <AuthLayout
      title="Esqueceu a senha?"
      subtitle="Insira seu e-mail e enviaremos um link para redefinição de senha."
      withNavbar
    >
      {success && (
        <div className="mb-4">
          <Alert variant="success" fullWidth onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </div>
      )}

      {error && (
        <div className="mb-4">
          <Alert variant="error" fullWidth onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      <form
        onSubmit={handleSubmit(ondSendRecoverPasswordEmail)}
        className="flex flex-col justify-between h-full grow"
      >
        <section className="md:mb-14">
          <Controller
            name="email"
            control={control}
            render={({
              field: { value = '', onChange },
              fieldState: { invalid, isDirty, error },
            }) => (
              <Input
                label="E-mail*"
                isValid={!invalid && isDirty}
                value={value}
                placeholder="exemple@email.com.br"
                onChange={onChange}
                errorMessage={error?.message}
              />
            )}
          />
        </section>

        <footer className="flex flex-col gap-4 md:flex-row-reverse">
          <div className="flex-1">
            <Button
              disabled={!formState.isValid}
              isLoading={formState.isSubmitting}
              type="submit"
              fullWidth
            >
              {formState.isSubmitting ? 'Enviando...' : 'Continuar'}
            </Button>
          </div>

          <div className="flex-1">
            <Button
              disabled={formState.isSubmitting}
              type="button"
              variant="outline"
              fullWidth
              onClick={() => navigate('/painel')}
            >
              Cancelar
            </Button>
          </div>
        </footer>
      </form>
    </AuthLayout>
  )
}
